import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Building2, FileLock2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DocRow } from "@/components/Docs";
import { DocRequestDialog } from "@/components/DocRequestDialog";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/api";

const LABELS = [
  ["legal_name", "Legal name"], ["trading_name", "Trading name"], ["registration_number", "Registration / ABN"], ["industry", "Industry"],
  ["website", "Website"], ["company_size", "Team size"], ["year_founded", "Founded"], ["address", "Address"], ["country", "Country"],
  ["contact_person", "Hiring contact"], ["contact_role", "Contact role"], ["contact_phone", "Phone"], ["working_hours", "Working hours"],
];

export default function BusinessView() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: b, loading } = useFetch(`/businesses/${id}`, [id]);
  const [req, setReq] = useState(false);
  if (loading) return <p className="text-stone-500">Loading…</p>;
  if (!b) return <p className="text-stone-500" data-testid="business-not-found">Business not available.</p>;
  const p = b.profile;
  const candidate = user?.role === "candidate";

  return (
    <div data-testid="business-view">
      <Link to={candidate ? "/candidate/offers" : "/admin/members"} className="mb-6 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900"><ArrowLeft className="h-4 w-4" />Back</Link>
      <header className="rounded-md border border-stone-200 bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-4">
          <div className="grid h-16 w-16 place-items-center rounded-md bg-slate-900 text-white"><Building2 className="h-7 w-7" /></div>
          <div className="flex-1">
            <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="business-name">{p.trading_name || b.company || b.name}</h1>
            <p className="text-sm text-stone-500">{p.industry}{p.country ? ` · ${p.country}` : ""}</p>
          </div>
          {candidate && <Button data-testid="request-business-files-button" variant="outline" onClick={() => setReq(true)}><FileLock2 className="mr-2 h-4 w-4" />Request files</Button>}
        </div>
        {p.about && <p className="mt-6 max-w-3xl leading-relaxed text-stone-700">{p.about}</p>}
      </header>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <section className="rounded-md border border-stone-200 bg-white p-6 lg:col-span-2">
          <h2 className="font-display text-xl font-bold">Business details</h2>
          <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {LABELS.filter(([k]) => p[k]).map(([k, l]) => <div key={k} className="border-b border-stone-100 pb-2"><dt className="text-[11px] font-mono uppercase text-stone-500">{l}</dt><dd className="text-sm font-medium">{p[k]}</dd></div>)}
          </dl>
          {p.hiring_needs && <><h3 className="mt-6 font-display font-bold">Hiring needs</h3><p className="mt-1 text-sm text-stone-700">{p.hiring_needs}</p></>}
          {p.benefits && <><h3 className="mt-6 font-display font-bold">Benefits</h3><p className="mt-1 text-sm text-stone-700">{p.benefits}</p></>}
        </section>
        <section className="h-fit rounded-md border border-stone-200 bg-white p-6">
          <h2 className="font-display text-xl font-bold">Licenses & hiring files</h2>
          <div className="mt-4 space-y-2">
            {b.documents.length === 0 && <p className="text-sm text-stone-500">No shared files yet. You can request them.</p>}
            {b.documents.map((d) => <DocRow key={d.id} d={d} />)}
          </div>
        </section>
      </div>
      {candidate && <DocRequestDialog open={req} onOpenChange={setReq} targetType="business" targetId={b.id} targetName={p.trading_name || b.company} />}
    </div>
  );
}
